import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowDownLeft,
  ArrowUpRight,
  TrendingUp,
  TrendingDown,
  Wallet,
  AlertTriangle,
  MessageSquareText,
  Upload as UploadIcon,
  FileText,
  ArrowRight,
  Smartphone,
  Copy,
  Building2,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLocation } from "wouter";
import React, { useMemo, useState, useEffect } from "react";
import { CATEGORY_LABELS, formatAmount } from "@shared/types";
import type { TransactionCategory } from "@shared/types";
import { useLanguage, CATEGORY_LABELS_AR } from "@/contexts/LanguageContext";
import { useChartTheme } from "@/hooks/useChartTheme";
import { useAuth } from "@/_core/hooks/useAuth";
import BusinessDashboard from "./BusinessDashboard";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

/** Format account label for display in dropdown.
 * Prefers accountLabel (user-set) > fallback formatting from accountKey.
 */
function formatAccountLabel(accountNumber: string, bankSource?: string | null, accountLabel?: string | null): string {
  // If user has set a custom label, use it
  if (accountLabel) return accountLabel;
  // Account numbers like "NBK-Main", "Boubyan-Main" → "NBK Account", "Boubyan Account"
  if (accountNumber.endsWith('-Main')) {
    const bank = accountNumber.replace('-Main', '');
    return `${bank} Account`;
  }
  // KFH accounts: "xx958" → "KFH ••958", "xx3373" → "KFH ••3373"
  if (accountNumber.startsWith('xx')) {
    const digits = accountNumber.replace('xx', '');
    const bank = bankSource?.includes('KFH') ? 'KFH' : (bankSource || '');
    return `${bank} ••${digits}`;
  }
  // "KFH-other" → "KFH Other"
  if (accountNumber === 'KFH-other') {
    return 'KFH Other';
  }
  // Numeric account: "0620" → "NBK 0620"
  if (/^\d+$/.test(accountNumber)) {
    const bank = bankSource || '';
    const shortBank = bank.includes('NBK') || bank.includes('National Bank') ? 'NBK'
      : bank.includes('Boubyan') ? 'Boubyan'
      : bank.includes('KFH') ? 'KFH'
      : bank;
    return `${shortBank} ${accountNumber}`;
  }
  return accountNumber;
}

/** Format amount for compact mobile display */
function mobileAmount(amount: number, currency?: string | null): string {
  const cur = currency ?? "KWD";
  const abs = Math.abs(amount);
  if (abs >= 1000) {
    return `${cur} ${abs.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
  }
  return `${cur} ${abs.toFixed(3)}`;
}

function WelcomeGuide({ onGoToSMS }: { onGoToSMS: () => void }) {
  const { t, language, isRTL } = useLanguage();

  return (
    <div className="space-y-6" dir={isRTL ? "rtl" : "ltr"}>
      <div className="text-center py-4">
        <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Wallet className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
        </div>
        <h2 className="text-lg sm:text-xl font-semibold text-foreground">{t("dashboard.emptyTitle")}</h2>
        <p className="text-muted-foreground mt-2 max-w-md mx-auto text-sm">
          {t("dashboard.emptyDesc")}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto">
        <Card className="relative overflow-hidden">
          <div className={`absolute top-3 ${isRTL ? "right-3" : "left-3"} h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs sm:text-sm font-bold`}>
            1
          </div>
          <CardContent className="pt-10 sm:pt-12 pb-4 sm:pb-6 text-center">
            <FileText className="h-8 w-8 sm:h-10 sm:w-10 mx-auto mb-2 sm:mb-3 text-muted-foreground" />
            <h3 className="font-medium text-foreground mb-1 text-sm sm:text-base">
              {language === "ar" ? "ارفع كشف حسابك" : "Upload a Statement"}
            </h3>
            <p className="text-xs text-muted-foreground">
              {language === "ar" ? "ارفع كشف حسابك البنكي (PDF أو Excel) لاستيراد معاملاتك تلقائياً" : "Upload your bank statement (PDF or Excel) to automatically import your transactions"}
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className={`absolute top-3 ${isRTL ? "right-3" : "left-3"} h-6 w-6 sm:h-7 sm:w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs sm:text-sm font-bold`}>
            2
          </div>
          <CardContent className="pt-10 sm:pt-12 pb-4 sm:pb-6 text-center">
            <Smartphone className="h-8 w-8 sm:h-10 sm:w-10 mx-auto mb-2 sm:mb-3 text-muted-foreground" />
            <h3 className="font-medium text-foreground mb-1 text-sm sm:text-base">
              {language === "ar" ? "أو ارفع رسائل SMS" : "Or Upload SMS"}
            </h3>
            <p className="text-xs text-muted-foreground">
              {language === "ar" ? "انسخ والصق رسائل البنك النصية أو ارفع لقطات شاشة" : "Copy and paste your bank SMS messages or upload screenshots"}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="text-center pb-4">
        <Button size="lg" onClick={onGoToSMS} className="gap-2">
          <UploadIcon className="h-5 w-5" />
          {t("dashboard.goToSMS")}
        </Button>
      </div>
    </div>
  );
}

const DASHBOARD_LOADING_TIMEOUT_MS = 15_000;

function useLoadingTimeout(isPending: boolean) {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!isPending) {
      setTimedOut((current) => current ? false : current);
      return;
    }
    const timer = window.setTimeout(() => setTimedOut(true), DASHBOARD_LOADING_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isPending]);

  return timedOut;
}

function DashboardQueryState({ retry, timedOut = false }: { retry: () => void; timedOut?: boolean }) {
  const { t, isRTL } = useLanguage();
  return (
    <div className="rounded-xl border border-amber-400/40 bg-amber-50/50 p-6 text-center dark:border-amber-500/30 dark:bg-amber-950/20" dir={isRTL ? "rtl" : "ltr"} role="alert">
      <AlertTriangle className="mx-auto mb-3 h-9 w-9 text-amber-600 dark:text-amber-400" />
      <h1 className="text-lg font-semibold text-foreground">{timedOut ? t("dashboard.queryTimeout") : t("dashboard.queryUnavailable")}</h1>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">{t("dashboard.queryRetryDescription")}</p>
      <Button className="mt-4" onClick={retry}>{t("dashboard.retry")}</Button>
    </div>
  );
}

function DashboardReconciliationNotice({ onReview }: { onReview: () => void }) {
  const { t, isRTL } = useLanguage();
  const { data: reconciliation } = trpc.transaction.reconciliationStatus.useQuery();
  const differenceCount = reconciliation?.summary.different ?? 0;

  if (differenceCount === 0) return null;

  const notice = differenceCount === 1
    ? t("reconcile.accountNeedsReview")
    : t("reconcile.accountsNeedReview").replace("{{count}}", String(differenceCount));

  return (
    <button type="button" onClick={onReview} className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg border border-amber-400/50 bg-amber-50/50 px-3 py-2 text-start text-sm text-amber-900 transition-colors hover:bg-amber-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:border-amber-500/30 dark:bg-amber-950/20 dark:text-amber-200" dir={isRTL ? "rtl" : "ltr"}>
      <span className="flex min-w-0 items-center gap-2"><AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />{notice}</span>
      <span className="shrink-0 text-xs font-medium underline underline-offset-2">{t("reconcile.reviewBalanceStatus")}</span>
    </button>
  );
}

function IndividualDashboard() {
  const [, setLocation] = useLocation();
  const { t, language, isRTL } = useLanguage();
  const { textColor: chartTextColor, gridColor: chartGridColor, tooltipBg, tooltipBorder, tooltipText, cursorColor, incomeColor, expenseColor } = useChartTheme();
  const now = useMemo(() => new Date(), []);
  const currentMonthKey = useMemo(() => `${now.getFullYear()}-${now.getMonth()}`, [now]);
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthKey);
  const [selectedAccount, setSelectedAccount] = useState<string>("all");

  const { data: availableMonths } = trpc.transaction.availableMonths.useQuery();
  const availableMonthKeys = `|${(availableMonths ?? []).map((month) => `${month.year}-${month.month - 1}`).join("|")}|`;
  const mostRecentAvailableMonth = availableMonths?.[0]
    ? `${availableMonths[0].year}-${availableMonths[0].month - 1}`
    : undefined;

  // Derive startDate/endDate from selected month
  const { startDate, endDate } = useMemo(() => {
    const [yearStr, monthStr] = selectedMonth.split("-");
    const year = parseInt(yearStr);
    const month = parseInt(monthStr);
    const start = new Date(year, month, 1).getTime();
    const end = new Date(year, month + 1, 0, 23, 59, 59, 999).getTime();
    return { startDate: start, endDate: end };
  }, [selectedMonth]);

  // Build month options from available data
  const monthOptions = useMemo(() => {
    if (!availableMonths || availableMonths.length === 0) {
      return [{ value: currentMonthKey, label: now.toLocaleString(language === "ar" ? "ar-KW" : "default", { month: "long", year: "numeric" }) }];
    }
    return availableMonths.map((m) => {
      const d = new Date(m.year, m.month - 1, 1);
      const label = d.toLocaleString(language === "ar" ? "ar-KW" : "default", { month: "long", year: "numeric" });
      return { value: `${m.year}-${m.month - 1}`, label };
    });
  }, [availableMonths, language, currentMonthKey, now]);

  // If current month has no data, default to the most recent month with data
  useEffect(() => {
    if (mostRecentAvailableMonth && !availableMonthKeys.includes(`|${currentMonthKey}|`) && selectedMonth === currentMonthKey) {
      setSelectedMonth(mostRecentAvailableMonth);
    }
  }, [availableMonthKeys, currentMonthKey, mostRecentAvailableMonth, selectedMonth]);

  const { data: accounts } = trpc.transaction.accounts.useQuery();

  const accountFilter = selectedAccount === "all" ? undefined : selectedAccount;

  const { data: summary, isLoading: summaryLoading, isError: summaryFailed, refetch: refetchSummary } = trpc.transaction.summary.useQuery({
    startDate,
    endDate,
    accountNumber: accountFilter,
  });
  const { data: alerts, isLoading: alertsLoading } = trpc.budget.alerts.useQuery();
  const { data: balance } = trpc.transaction.latestBalance.useQuery({ startDate, endDate, accountNumber: accountFilter });
  const { data: recentTxns, isLoading: txnsLoading, isError: txnsFailed, refetch: refetchTransactions } = trpc.transaction.list.useQuery({
    limit: 5,
    offset: 0,
    startDate,
    endDate,
    accountNumber: accountFilter,
  });
  const { data: categoryData } = trpc.transaction.categoryBreakdown.useQuery({
    type: "cash_out",
    startDate,
    endDate,
    accountNumber: accountFilter,
  });
  const { data: earningData } = trpc.transaction.categoryBreakdown.useQuery({
    type: "cash_in",
    startDate,
    endDate,
    accountNumber: accountFilter,
  });

  // Monthly trend data (last 6 months)
  const monthlyTrendRanges = useMemo(() => {
    const [yearStr, monthStr] = selectedMonth.split("-");
    const y = parseInt(yearStr);
    const m = parseInt(monthStr);
    const ranges = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(y, m - i, 1);
      const mStart = d.getTime();
      const mEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
      ranges.push({
        label: d.toLocaleString(language === "ar" ? "ar-KW" : "default", { month: "short" }),
        start: mStart,
        end: mEnd,
      });
    }
    return ranges;
  }, [selectedMonth, language]);

  const { data: mt0 } = trpc.transaction.summary.useQuery({ startDate: monthlyTrendRanges[0]?.start ?? 0, endDate: monthlyTrendRanges[0]?.end ?? 0, accountNumber: accountFilter });
  const { data: mt1 } = trpc.transaction.summary.useQuery({ startDate: monthlyTrendRanges[1]?.start ?? 0, endDate: monthlyTrendRanges[1]?.end ?? 0, accountNumber: accountFilter });
  const { data: mt2 } = trpc.transaction.summary.useQuery({ startDate: monthlyTrendRanges[2]?.start ?? 0, endDate: monthlyTrendRanges[2]?.end ?? 0, accountNumber: accountFilter });
  const { data: mt3 } = trpc.transaction.summary.useQuery({ startDate: monthlyTrendRanges[3]?.start ?? 0, endDate: monthlyTrendRanges[3]?.end ?? 0, accountNumber: accountFilter });
  const { data: mt4 } = trpc.transaction.summary.useQuery({ startDate: monthlyTrendRanges[4]?.start ?? 0, endDate: monthlyTrendRanges[4]?.end ?? 0, accountNumber: accountFilter });
  const { data: mt5 } = trpc.transaction.summary.useQuery({ startDate: monthlyTrendRanges[5]?.start ?? 0, endDate: monthlyTrendRanges[5]?.end ?? 0, accountNumber: accountFilter });

  const monthlyTrendData = useMemo(() => {
    const summaries = [mt0, mt1, mt2, mt3, mt4, mt5];
    return monthlyTrendRanges.map((r, i) => {
      const s = summaries[i];
      return {
        month: r.label,
        cashIn: parseFloat(s?.find((x) => x.type === "cash_in")?.total ?? "0"),
        cashOut: parseFloat(s?.find((x) => x.type === "cash_out")?.total ?? "0"),
      };
    });
  }, [monthlyTrendRanges, mt0, mt1, mt2, mt3, mt4, mt5]);

  const getCategoryLabel = (cat: string) => {
    // Handle legacy category names
    const normalized = cat === "bills_utilities" ? "bills" : cat;
    if (language === "ar") {
      return CATEGORY_LABELS_AR[normalized] ?? CATEGORY_LABELS[normalized as TransactionCategory] ?? normalized.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    }
    return CATEGORY_LABELS[normalized as TransactionCategory] ?? normalized.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  };

  const cashIn = summary?.find((s) => s.type === "cash_in");
  const cashOut = summary?.find((s) => s.type === "cash_out");
  const totalIn = parseFloat(cashIn?.total ?? "0");
  const totalOut = parseFloat(cashOut?.total ?? "0");
  const netFlow = totalIn - totalOut;

  const monthName = useMemo(() => {
    const [yearStr, monthStr] = selectedMonth.split("-");
    const d = new Date(parseInt(yearStr), parseInt(monthStr), 1);
    return d.toLocaleString(language === "ar" ? "ar-KW" : "default", { month: "long", year: "numeric" });
  }, [selectedMonth, language]);

  const hasTransactions = !txnsLoading && recentTxns && recentTxns.length > 0;
  const isNewUser = !summaryLoading && !txnsLoading && !hasTransactions;
  const dashboardQueryTimedOut = useLoadingTimeout(summaryLoading || txnsLoading);
  const dashboardQueryFailed = summaryFailed || txnsFailed;

  if (dashboardQueryFailed || dashboardQueryTimedOut) {
    return <DashboardQueryState timedOut={dashboardQueryTimedOut} retry={() => { void Promise.all([refetchSummary(), refetchTransactions()]); }} />;
  }

  if (isNewUser) {
    return (
      <div className="space-y-4 sm:space-y-6" dir={isRTL ? "rtl" : "ltr"}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">{t("dashboard.title")}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {language === "ar" ? "ابدأ مع ميزانو" : "Get started with Mizano"}
            </p>
          </div>
        </div>
        <WelcomeGuide onGoToSMS={() => setLocation("/sms")} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">{t("dashboard.title")}</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 sm:mt-1">
            {language === "ar" ? `نظرة عامة - ${monthName}` : `${monthName} Overview`}
          </p>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 flex-wrap">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[120px] sm:w-[180px] h-8 sm:h-9 text-[11px] sm:text-sm">
              <SelectValue placeholder={monthName} />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedAccount} onValueChange={setSelectedAccount}>
            <SelectTrigger className="w-[120px] sm:w-[180px] h-8 sm:h-9 text-[11px] sm:text-sm">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 hidden sm:block" />
              <SelectValue placeholder={t("dashboard.allAccounts")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("dashboard.allAccounts")}</SelectItem>
              {accounts && accounts.filter((acc) => acc.accountNumber != null && acc.isActive !== 0).map((acc) => (
                <SelectItem key={acc.accountNumber!} value={acc.accountNumber!}>
                  {formatAccountLabel(acc.accountNumber!, acc.bankSource, acc.accountLabel)}
                  <span className="ml-1 text-muted-foreground">({acc.count})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setLocation("/sms")} size="sm" className="gap-1.5 flex-shrink-0">
            <UploadIcon className="h-4 w-4" />
            <span className="hidden sm:inline">{language === "ar" ? "رفع المعاملات" : "Upload"}</span>
            <span className="sm:hidden">{language === "ar" ? "رفع" : "Upload"}</span>
          </Button>
        </div>
      </div>

      {/* Summary Overview — gradient background like Budgets */}
      <div className="rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-7 w-7 rounded-lg bg-primary/20 flex items-center justify-center">
            <Wallet className="h-4 w-4 text-primary" />
          </div>
          <h2 className="text-sm font-semibold text-foreground">
            {language === "ar" ? "ملخص مالي" : "Financial Summary"}
          </h2>
        </div>
        {summaryLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
            <div
              className="text-center p-2.5 sm:p-3 rounded-lg bg-background/60 backdrop-blur-sm cursor-pointer hover:bg-background/80 transition-colors"
              onClick={() => setLocation(`/transactions?type=cash_in&month=${selectedMonth}${selectedAccount !== 'all' ? `&account=${encodeURIComponent(selectedAccount)}` : ''}`)}
            >
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5">{t("dashboard.cashIn")}</p>
              <p className="text-xs sm:text-lg font-bold leading-tight whitespace-nowrap" style={{ color: "var(--income)" }}>
                {mobileAmount(totalIn)}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{cashIn?.count ?? 0} {t("dashboard.transactions")}</p>
            </div>
            <div
              className="text-center p-2.5 sm:p-3 rounded-lg bg-background/60 backdrop-blur-sm cursor-pointer hover:bg-background/80 transition-colors"
              onClick={() => setLocation(`/transactions?type=cash_out&month=${selectedMonth}${selectedAccount !== 'all' ? `&account=${encodeURIComponent(selectedAccount)}` : ''}`)}
            >
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5">{t("dashboard.cashOut")}</p>
              <p className="text-xs sm:text-lg font-bold leading-tight whitespace-nowrap" style={{ color: "var(--expense)" }}>
                {mobileAmount(totalOut)}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{cashOut?.count ?? 0} {t("dashboard.transactions")}</p>
            </div>
            <div
              className="text-center p-2.5 sm:p-3 rounded-lg cursor-pointer hover:opacity-80 transition-colors"
              style={{ backgroundColor: netFlow >= 0 ? "oklch(0.85 0.12 155 / 0.25)" : "oklch(0.75 0.12 25 / 0.25)" }}
              onClick={() => setLocation("/income-statement")}
            >
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5">{t("dashboard.netCashFlow")}</p>
              <p className="text-xs sm:text-lg font-bold leading-tight whitespace-nowrap" style={{ color: netFlow >= 0 ? "oklch(0.55 0.18 155)" : "oklch(0.55 0.18 25)" }}>
                {netFlow >= 0 ? "+" : "-"}{mobileAmount(Math.abs(netFlow))}
              </p>
              <p className="text-[10px] mt-0.5" style={{ color: netFlow >= 0 ? "oklch(0.55 0.18 155)" : "oklch(0.55 0.18 25)" }}>
                {netFlow >= 0 ? (language === "ar" ? "فائض" : "Surplus") : (language === "ar" ? "عجز" : "Deficit")}
              </p>
            </div>
            <div className="text-center p-2.5 sm:p-3 rounded-lg bg-background/60 backdrop-blur-sm">
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5">{t("dashboard.latestBalance")}</p>
              <p className="text-xs sm:text-lg font-bold text-foreground leading-tight whitespace-nowrap">
                {balance != null ? mobileAmount(balance) : "—"}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {language === "ar" ? "آخر رصيد للشهر" : "End of month"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Budget Alerts — amber accent like Budgets page */}
      {!alertsLoading && alerts && alerts.length > 0 && (
        <div className="rounded-xl border-2 border-amber-400/50 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20 p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <h2 className="text-sm font-semibold text-amber-700 dark:text-amber-400">
              {t("budgets.alerts")}
            </h2>
          </div>
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div
                key={alert.category}
                className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg bg-amber-100/60 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-800/30 gap-2"
              >
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-foreground truncate">
                    {getCategoryLabel(alert.category)}
                  </p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">
                    {mobileAmount(parseFloat(String(alert.spent)))} / {mobileAmount(parseFloat(String(alert.limit)))}
                  </p>
                </div>
                <Badge variant={alert.percentage >= 100 ? "destructive" : "secondary"} className="flex-shrink-0 text-[10px] sm:text-xs">
                  {alert.percentage}%
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      <DashboardReconciliationNotice onReview={() => setLocation("/account?tab=balance")} />

      {/* Top Spending Categories */}
      <div className="rounded-xl border border-border bg-card p-4 sm:p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">{t("dashboard.topSpending")}</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setLocation(`/transactions?type=cash_out&month=${selectedMonth}${selectedAccount !== 'all' ? `&account=${encodeURIComponent(selectedAccount)}` : ''}`)} className="gap-1 text-[10px] sm:text-xs h-7 sm:h-8 px-2">
            {language === "ar" ? "التفاصيل" : "Details"} <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
        {categoryData && categoryData.length > 0 ? (
          <div className="space-y-2.5 sm:space-y-3">
            {categoryData.slice(0, 5).map((cat) => {
              const total = parseFloat(cat.total ?? "0");
              const maxTotal = parseFloat(categoryData[0]?.total ?? "1");
              const pct = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
              return (
                <div
                  key={cat.category}
                  className="p-2.5 sm:p-3 rounded-lg bg-background/80 border border-border/50 cursor-pointer hover:bg-background/95 hover:border-primary/30 transition-colors"
                  onClick={() => setLocation(`/transactions?type=cash_out&month=${selectedMonth}&category=${encodeURIComponent(cat.category ?? 'uncategorized')}${selectedAccount !== 'all' ? `&account=${encodeURIComponent(selectedAccount)}` : ''}`)}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs sm:text-sm text-foreground font-medium truncate">
                      {getCategoryLabel(cat.category ?? "uncategorized")}
                    </span>
                    <span className="text-[10px] sm:text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                      {mobileAmount(total)} ({cat.count})
                    </span>
                  </div>
                  <div className="h-1.5 sm:h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: "var(--expense)",
                        opacity: 0.7 + (pct / 100) * 0.3,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 sm:py-8 text-muted-foreground">
            <TrendingDown className="h-7 w-7 sm:h-8 sm:w-8 mx-auto mb-2 opacity-50" />
            <p className="text-xs sm:text-sm">{t("dashboard.noSpending")}</p>
          </div>
        )}
      </div>

      {/* Top Earning Categories */}
      <div className="rounded-xl border border-border bg-card p-4 sm:p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">{t("dashboard.topEarning")}</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setLocation(`/transactions?type=cash_in&month=${selectedMonth}${selectedAccount !== 'all' ? `&account=${encodeURIComponent(selectedAccount)}` : ''}`)} className="gap-1 text-[10px] sm:text-xs h-7 sm:h-8 px-2">
            {language === "ar" ? "التفاصيل" : "Details"} <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
        {earningData && earningData.length > 0 ? (
          <div className="space-y-2.5 sm:space-y-3">
            {earningData.slice(0, 5).map((cat) => {
              const total = parseFloat(cat.total ?? "0");
              const maxTotal = parseFloat(earningData[0]?.total ?? "1");
              const pct = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
              return (
                <div
                  key={cat.category}
                  className="p-2.5 sm:p-3 rounded-lg bg-background/80 border border-border/50 cursor-pointer hover:bg-background/95 hover:border-primary/30 transition-colors"
                  onClick={() => setLocation(`/transactions?type=cash_in&month=${selectedMonth}&category=${encodeURIComponent(cat.category ?? 'uncategorized')}${selectedAccount !== 'all' ? `&account=${encodeURIComponent(selectedAccount)}` : ''}`)}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs sm:text-sm text-foreground font-medium truncate">
                      {getCategoryLabel(cat.category ?? "uncategorized")}
                    </span>
                    <span className="text-[10px] sm:text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
                      {mobileAmount(total)} ({cat.count})
                    </span>
                  </div>
                  <div className="h-1.5 sm:h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: "var(--income)",
                        opacity: 0.7 + (pct / 100) * 0.3,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 sm:py-8 text-muted-foreground">
            <TrendingUp className="h-7 w-7 sm:h-8 sm:w-8 mx-auto mb-2 opacity-50" />
            <p className="text-xs sm:text-sm">{t("dashboard.noEarning")}</p>
          </div>
        )}
      </div>

      {/* Monthly Trend Chart */}
      <div className="rounded-xl border border-border bg-card p-4 sm:p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">
            {language === "ar" ? "الاتجاه الشهري" : "Monthly Trend"}
          </h2>
        </div>
        {monthlyTrendData.some(d => d.cashIn > 0 || d.cashOut > 0) ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthlyTrendData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartGridColor} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: chartTextColor }} axisLine={{ stroke: chartGridColor }} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: chartTextColor }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Tooltip
                contentStyle={{ backgroundColor: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: "8px", fontSize: "12px", color: tooltipText }}
                labelStyle={{ color: tooltipText, fontWeight: 600, marginBottom: "4px" }}
                itemStyle={{ color: tooltipText }}
                formatter={(value: number, name: string) => [formatAmount(value), name === "cashIn" ? (language === "ar" ? "الدخل" : "Cash In") : (language === "ar" ? "المصروفات" : "Cash Out")]}
                cursor={{ fill: cursorColor }}
              />
              <Bar dataKey="cashIn" fill={incomeColor} radius={[4, 4, 0, 0]} name="cashIn" />
              <Bar dataKey="cashOut" fill={expenseColor} radius={[4, 4, 0, 0]} name="cashOut" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <TrendingUp className="h-7 w-7 mx-auto mb-2 opacity-50" />
            <p className="text-xs">{language === "ar" ? "لا توجد بيانات" : "No data available"}</p>
          </div>
        )}
      </div>

      {/* Recent Transactions — left accent border */}
      <Card className="border-l-4 border-l-primary">
        <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6 pt-3 sm:pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowDownLeft className="h-4 w-4 text-primary" />
              <CardTitle className="text-sm sm:text-base">{t("dashboard.recentTransactions")}</CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setLocation("/transactions")} className="gap-1 text-[10px] sm:text-xs h-7 sm:h-8 px-2">
              {t("common.viewAll")} <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
          {txnsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : recentTxns && recentTxns.length > 0 ? (
            <div className="space-y-1.5 sm:space-y-2">
              {recentTxns.map((txn) => (
                <div key={txn.id} className="flex items-center justify-between p-2 sm:p-3 rounded-lg bg-muted/50 gap-2">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div
                      className="h-7 w-7 sm:h-8 sm:w-8 rounded-full flex-shrink-0 flex items-center justify-center"
                      style={{
                        backgroundColor: txn.type === "cash_in"
                          ? "oklch(0.65 0.18 155 / 0.12)"
                          : "oklch(0.58 0.22 25 / 0.12)",
                      }}
                    >
                      {txn.type === "cash_in" ? (
                        <ArrowDownLeft className="h-3 w-3 sm:h-4 sm:w-4" style={{ color: "var(--income)" }} />
                      ) : (
                        <ArrowUpRight className="h-3 w-3 sm:h-4 sm:w-4" style={{ color: "var(--expense)" }} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-foreground truncate">{txn.merchant ?? "Unknown"}</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground">
                        {getCategoryLabel(txn.category as string)}
                      </p>
                    </div>
                  </div>
                  <div className={`${isRTL ? "text-left" : "text-right"} flex-shrink-0`}>
                    <p
                      className="text-xs sm:text-sm font-semibold whitespace-nowrap"
                      style={{ color: txn.type === "cash_in" ? "var(--income)" : "var(--expense)" }}
                    >
                      {txn.type === "cash_in" ? "+" : "-"}{mobileAmount(parseFloat(String(txn.amount)), txn.currency)}
                    </p>
                    {txn.currency && txn.currency !== "KWD" && txn.amountInKwd && (
                      <p className="text-[10px] sm:text-xs text-muted-foreground">
                        ≈ {mobileAmount(parseFloat(String(txn.amountInKwd)), "KWD")}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8 text-muted-foreground">
              <MessageSquareText className="h-7 w-7 sm:h-8 sm:w-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs sm:text-sm">{t("dashboard.noTransactions")}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** The existing personal dashboard is rendered unchanged for Individual sessions. */
export default function Home() {
  const { loading } = useAuth();
  const workspace = trpc.auth.workspace.useQuery(undefined, { enabled: !loading });
  const workspaceTimedOut = useLoadingTimeout(!loading && (workspace.isLoading || workspace.isFetching));
  // A cached workspace can briefly describe the previous signed session while this request refreshes.
  // Do not mount a business-only page until the current cookie's workspace has been confirmed.
  if (workspace.isError || workspaceTimedOut) return <DashboardQueryState timedOut={workspaceTimedOut} retry={() => { void workspace.refetch(); }} />;
  if (loading || workspace.isLoading || workspace.isFetching) return <Skeleton className="h-80 w-full" />;
  return workspace.data?.activeWorkspace === "business" ? <BusinessDashboard /> : <IndividualDashboard />;
}
